import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { resolveAvatar } from "@/lib/api";
import { House, Map, Target, Clapperboard, Medal, Star, Mic, Trophy, Award, User, Sparkles, LogOut, Bell } from "lucide-react";
import { InstallButton } from "@/components/InstallPWA";
import useKeyboardShortcuts from "@/hooks/useKeyboardShortcuts";

const NAV = [
  { to: "/app/home", Icon: House, label: "Home" },
  { to: "/app/journey", Icon: Map, label: "Journey" },
  { to: "/app/quests", Icon: Target, label: "Quests" },
  { to: "/app/replays", Icon: Clapperboard, label: "Replays" },
  { to: "/app/badges", Icon: Medal, label: "Badges" },
  { to: "/app/points", Icon: Star, label: "Points" },
  { to: "/app/stories", Icon: Mic, label: "My Stories" },
  { to: "/app/leaderboard", Icon: Trophy, label: "Leaderboard" },
  { to: "/app/performance", Icon: Award, label: "Grand Finale" },
  { to: "/app/profile", Icon: User, label: "Profile" },
];

const MOBILE_NAV = [NAV[0], NAV[1], NAV[2], NAV[4], NAV[9]];

function useUnread() {
  const { data } = useQuery({
    queryKey: ["notifications"], queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 20000,
  });
  return (data || []).filter((n) => !n.read).length;
}

export default function StudentLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const unread = useUnread();
  useKeyboardShortcuts("student");

  return (
    <div className="mx-auto flex min-h-screen max-w-7xl">
      <aside className="hidden w-64 flex-col gap-1 border-r border-black/5 bg-white/70 p-4 lg:flex sticky top-0 h-screen">
        <div className="mb-6 flex items-center gap-2 px-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl gradient-orange">
            <Sparkles size={20} className="text-white" />
          </div>
          <div className="font-head text-lg leading-none text-csh-blue">Story<span className="text-csh-orange">Champs</span></div>
        </div>
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3 py-2.5 font-semibold transition-colors ${
                isActive ? "gradient-purple text-white" : "text-csh-grey hover:bg-black/5"}`}>
            <n.Icon size={20} />
            {n.label}
          </NavLink>
        ))}
        <button data-testid="logout-btn" onClick={() => { logout(); navigate("/login"); }}
          className="mt-auto flex items-center gap-3 rounded-xl px-3 py-2.5 font-semibold text-csh-grey hover:bg-black/5">
          <LogOut size={20} /> Sign Out
        </button>
      </aside>

      <div className="flex-1 pb-24 lg:pb-6">
        <header className="sticky top-0 z-30 flex items-center justify-between gap-3 bg-background/90 px-4 py-3 backdrop-blur-md lg:px-8">
          <div className="flex items-center gap-2 lg:hidden">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg gradient-orange">
              <Sparkles size={16} className="text-white" />
            </div>
            <span className="font-head text-csh-blue">StoryChamps</span>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <InstallButton className="hidden sm:flex" />
            <NavLink to="/app/notifications" data-testid="notif-bell" className="relative rounded-full bg-white p-2.5 shadow-sm border border-black/5">
              <Bell size={20} className="text-csh-blue" />
              {unread > 0 && (
                <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-csh-orange px-1 text-[10px] font-bold text-white">{unread}</span>
              )}
            </NavLink>
            <NavLink to="/app/profile" className="flex items-center gap-2 rounded-full bg-white p-1 pr-3 shadow-sm border border-black/5">
              <img src={resolveAvatar(user?.avatar)} alt="" className="h-8 w-8 rounded-full object-cover" />
              <span className="hidden font-semibold text-csh-blue sm:block">{user?.name?.split(" ")[0]}</span>
            </NavLink>
          </div>
        </header>
        <motion.main initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
          className="px-4 py-4 lg:px-8">
          {children}
        </motion.main>
      </div>

      <nav className="fixed bottom-0 left-0 right-0 z-40 flex items-center justify-around border-t border-black/5 bg-white/95 px-2 py-2 backdrop-blur-md lg:hidden" style={{ paddingBottom: "calc(0.5rem + env(safe-area-inset-bottom))" }}>
        {MOBILE_NAV.map((n) => (
          <NavLink key={n.to} to={n.to} data-testid={`mnav-${n.label.toLowerCase().replace(/ /g, "-")}`}
            className={({ isActive }) =>
              `flex flex-1 flex-col items-center gap-0.5 rounded-xl py-1.5 text-[10px] font-bold ${
                isActive ? "text-csh-purple" : "text-csh-grey/60"}`}>
            <n.Icon size={22} /> {n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
